/**
 * WS-NAV — the React bindings over the pure resolvers in `resolve.ts`.
 *
 * Everything decision-shaped lives in `resolve.ts` (and is unit-tested there);
 * these hooks only wire it to the role context, the router location and the
 * persisted last-used workspace.
 */
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import { useCurrentUserRole, useHasRole } from "@aeriosiq/auth-client";
import { useCanReadContacts } from "../../features/admin/contacts/index.js";
import { useUiStore } from "../uiStore.js";
import {
  resolveDisplayWorkspaceId,
  resolveOwningWorkspaceId,
  resolveLandingPath,
  startPathFor,
  visibleItems,
  visibleWorkspaces,
} from "./resolve.js";
import { workspaceById } from "./workspaces.js";
import type {
  Workspace,
  WorkspaceAccess,
  WorkspaceId,
  WorkspaceNavItem,
} from "./types.js";

/**
 * The caller's role + the leaf gates, in one object. Both gates resolve
 * through the sanctioned primitives here (SB2-CC-01 AC9) so the pure layer in
 * `resolve.ts` never compares a role literal itself.
 */
export function useWorkspaceAccess(): WorkspaceAccess {
  const { role } = useCurrentUserRole();
  const canReadContacts = useCanReadContacts();
  const isTenantAdmin = useHasRole("tenant_admin");
  return useMemo(
    () => ({ role, canReadContacts, isTenantAdmin }),
    [role, canReadContacts, isTenantAdmin],
  );
}

/** The workspaces offered to the caller, in canonical order. */
export function useVisibleWorkspaces(): readonly Workspace[] {
  const access = useWorkspaceAccess();
  return useMemo(() => visibleWorkspaces(access), [access]);
}

export interface ActiveWorkspace {
  readonly access: WorkspaceAccess;
  readonly visible: readonly Workspace[];
  /** `null` before the role resolves (or for a role with no workspace). */
  readonly workspace: Workspace | null;
  /** The active workspace's nav items, already role-filtered. */
  readonly items: readonly WorkspaceNavItem[];
  /** Where the switcher should send the user for a given workspace. */
  readonly startPathOf: (workspace: Workspace) => string;
  /**
   * Open a workspace the user explicitly chose (switcher, Home card, shortcut).
   *
   * Path resolution alone cannot answer "which workspace did they mean?" when a
   * destination is owned by more than one — `/data-acquisitions` is Analysis's
   * start page AND a Projects nav entry. So the choice rides along with the
   * navigation as router location state.
   *
   * Deliberately NOT "write the store, then navigate": the store update and the
   * location change land in different renders, and in between, a resolver run
   * against the OLD pathname would overwrite the choice (leaving Projects for
   * Analysis wrote `analysis`, then `/` — Projects-owned — immediately put it
   * back). Tying the intent to the destination removes the window entirely.
   */
  readonly navigateToWorkspace: (workspace: Workspace) => void;
}

/** Router location-state key carrying an explicit workspace choice. */
export interface WorkspaceIntentState {
  readonly workspaceIntent?: WorkspaceId;
}

/**
 * The workspace the chrome should currently show.
 *
 * Rule 4 in practice: the pathname decides, the previous workspace breaks ties
 * for routes two workspaces own, and the answer is written back to the store
 * so it doubles as "last used" for rule 3.
 */
export function useActiveWorkspace(): ActiveWorkspace {
  const access = useWorkspaceAccess();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const storedId = useUiStore((s) => s.activeWorkspaceId);
  const setActiveWorkspaceId = useUiStore((s) => s.setActiveWorkspaceId);

  // An explicit choice travels with the navigation rather than through the
  // store, so no intermediate render can overwrite it. See navigateToWorkspace.
  const intentId =
    (location.state as WorkspaceIntentState | null)?.workspaceIntent ?? null;

  const visible = useMemo(() => visibleWorkspaces(access), [access]);
  const args = useMemo(
    () => ({ pathname, access, currentId: storedId, intentId }),
    [pathname, access, storedId, intentId],
  );
  // Only a route the user actually navigated INTO updates "last used"; the
  // display fallback must not, or a first-time user would silently acquire a
  // remembered workspace and never reach Home.
  const owningId = useMemo(() => resolveOwningWorkspaceId(args), [args]);
  const displayId = useMemo(() => resolveDisplayWorkspaceId(args), [args]);

  useEffect(() => {
    if (owningId !== null && owningId !== storedId) {
      setActiveWorkspaceId(owningId);
    }
  }, [owningId, storedId, setActiveWorkspaceId]);

  const workspace = useMemo(
    () => (displayId === null ? null : workspaceById(displayId)),
    [displayId],
  );
  const items = useMemo(
    () => (workspace ? visibleItems(workspace, access) : []),
    [workspace, access],
  );
  const startPathOf = useMemo(
    () => (w: Workspace) => startPathFor(w, access),
    [access],
  );
  const navigateToWorkspace = useMemo(
    () => (w: Workspace) => {
      const state: WorkspaceIntentState = { workspaceIntent: w.id };
      void navigate(startPathFor(w, access), { state });
    },
    [access, navigate],
  );

  return {
    access,
    visible,
    workspace,
    items,
    startPathOf,
    navigateToWorkspace,
  };
}

/**
 * Where the app should open for this caller (rules 2 + 3). Used by `/home`,
 * which sends single-workspace and returning users straight on.
 */
export function useLandingPath(): string {
  const access = useWorkspaceAccess();
  const storedId = useUiStore((s) => s.activeWorkspaceId);
  return useMemo(
    () => resolveLandingPath({ access, lastUsedId: storedId }),
    [access, storedId],
  );
}

/**
 * True below MUI's `md` breakpoint (900 px) — the phone/small-tablet chrome.
 * Centralised here so "what counts as mobile" is one decision, per
 * mobile_strategy §2.3.
 */
export function useIsMobileViewport(): boolean {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
}
